import { useEffect, useState } from "react";
import { Phone, MessageCircle, Mail, Instagram } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const GOALS = ["Weight Loss", "Fat Loss", "Strength", "Muscle Gain", "General Fitness", "Running", "HYROX Preparation"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", goal: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({ whatsapp: "", phone: "" });
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setBusy(true);
    try {
      await api.post("/leads", form);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", goal: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader overline="Get In Touch" title="Let's talk" subtitle="Tell us about your goals and we'll reach out. Prefer to chat now? Message us directly." />
      <section className="mx-auto max-w-[1400px] px-5 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div className="space-y-4">
          {cfg.whatsapp && (
            <a data-testid="contact-whatsapp" href={`https://wa.me/${cfg.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 border border-border rounded-lg p-5 hover:border-primary transition-colors group">
              <MessageCircle className="text-[#25D366]" />
              <div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">WhatsApp</div><div className="font-bold group-hover:text-primary transition-colors">Chat with us instantly</div></div>
            </a>
          )}
          {cfg.phone && (
            <a data-testid="contact-phone" href={`tel:${cfg.phone}`} className="flex items-center gap-4 border border-border rounded-lg p-5 hover:border-primary transition-colors group">
              <Phone className="text-primary" /><div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Call</div><div className="font-bold group-hover:text-primary transition-colors">{cfg.phone}</div></div>
            </a>
          )}
          <a href="mailto:abhishar2010@gmail.com" className="flex items-center gap-4 border border-border rounded-lg p-5 hover:border-primary transition-colors group">
            <Mail className="text-primary" /><div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</div><div className="font-bold group-hover:text-primary transition-colors">abhishar2010@gmail.com</div></div>
          </a>
          <a href="https://instagram.com/beingfitforu_personal_training" target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 border border-border rounded-lg p-5 hover:border-primary transition-colors group">
            <Instagram className="text-primary" /><div><div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Instagram</div><div className="font-bold group-hover:text-primary transition-colors">@beingfitforu_personal_training</div></div>
          </a>
        </div>
        <div className="border border-border rounded-lg bg-card p-7 md:p-9">
          <h2 className="font-display font-800 uppercase text-3xl tracking-tight mb-7">Send a message</h2>
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Name</Label><Input data-testid="contact-name" value={form.name} onChange={set("name")} className="mt-2 h-12" placeholder="Your name" /></div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Email</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className="mt-2 h-12" placeholder="you@email.com" /></div>
              <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Phone</Label><Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className="mt-2 h-12" placeholder="Optional" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Goal</Label>
              <Select value={form.goal} onValueChange={set("goal")}>
                <SelectTrigger data-testid="contact-goal" className="mt-2 h-12"><SelectValue placeholder="Select a goal" /></SelectTrigger>
                <SelectContent>{GOALS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Message</Label><Textarea data-testid="contact-message" rows={4} value={form.message} onChange={set("message")} className="mt-2" placeholder="Tell us where you're at…" /></div>
            <button data-testid="contact-submit-button" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform disabled:opacity-60">{busy ? "Sending…" : "Send Message"}</button>
          </form>
        </div>
      </section>
    </div>
  );
}
